import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { Feather, Mail, Lock, BookOpen, User, LogOut, ChevronRight, Inbox, Archive, Clock, CheckCircle, Library } from 'lucide-react';

import { API } from '../config';

export default function ArchivePage() {
  const { user, token, isSubscribed, logout } = useAuth();
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received');

  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    try {
      const response = await axios.get(`${API}/letters`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setLetters(response.data);
    } catch (error) {
      console.error('Error fetching letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Create array of 52 weeks
  const allWeeks = Array.from({ length: 52 }, (_, i) => i + 1);

  const getLetterForWeek = (week) => {
    return letters.find(l => l.week_number === week);
  };

  const isLetterAccessible = (letter) => {
    if (!letter) return false;
    if (letter.teaser) return true;
    return isSubscribed;
  };

  const getLetterTypeLabel = (type) => {
    switch (type) {
      case 'diary': return 'Diary Entry';
      case 'unsent': return 'Unsent Letter';
      case 'interlude': return 'Interlude';
      default: return 'Letter';
    }
  };

  // Get received letters (accessible ones)
  const receivedLetters = letters.filter(l => isLetterAccessible(l));
  
  // Get upcoming letters (not yet accessible or not yet released)
  const upcomingWeeks = allWeeks.filter(week => {
    const letter = getLetterForWeek(week);
    return !letter || !isLetterAccessible(letter);
  });

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-parchment/95 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_ink-nostalgia/artifacts/xhguxwkc_34799.png"
              alt="Unspoken Hearts"
              className="h-10"
            />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/library" 
              className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
              data-testid="library-link"
            >
              <Library className="w-5 h-5" />
              <span className="font-lora hidden sm:inline">Library</span>
            </Link>
            <Link 
              to="/account" 
              className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
              data-testid="account-link"
            >
              <User className="w-5 h-5" />
              <span className="font-lora hidden sm:inline">Account</span>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-charcoal-muted hover:text-charcoal"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-8 animate-fade-in">
          <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">Your Letters</h1>
          <p className="font-lora text-lg text-charcoal-muted max-w-2xl">
            {isSubscribed 
              ? "Your personal collection of letters from Clara and James. New letters arrive every week."
              : "Subscribe to receive the full story delivered to your inbox weekly."
            }
          </p>
          
          {!isSubscribed && (
            <Button 
              onClick={() => navigate('/subscription')}
              className="btn-primary mt-6"
              data-testid="subscribe-cta-btn"
            >
              Subscribe for $7/month
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </header>

        {/* Tabs */}
        <Tabs defaultValue="received" className="animate-fade-in delay-100" onValueChange={setActiveTab}>
          <TabsList className="bg-parchment-paper border border-[#E5E0D0] mb-8">
            <TabsTrigger 
              value="received" 
              className="font-lora data-[state=active]:bg-parchment data-[state=active]:text-charcoal"
              data-testid="received-tab"
            >
              <Inbox className="w-4 h-4 mr-2" />
              Received ({receivedLetters.length})
            </TabsTrigger>
            <TabsTrigger 
              value="archive" 
              className="font-lora data-[state=active]:bg-parchment data-[state=active]:text-charcoal"
              data-testid="archive-tab"
            >
              <Archive className="w-4 h-4 mr-2" />
              Full Archive (52)
            </TabsTrigger>
            <TabsTrigger 
              value="upcoming" 
              className="font-lora data-[state=active]:bg-parchment data-[state=active]:text-charcoal"
              data-testid="upcoming-tab"
            >
              <Clock className="w-4 h-4 mr-2" />
              Upcoming ({upcomingWeeks.length})
            </TabsTrigger>
          </TabsList>

          {/* Received Letters Tab */}
          <TabsContent value="received">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Feather className="w-8 h-8 text-wine mx-auto mb-4 animate-pulse" />
                  <p className="font-lora text-charcoal-muted">Loading your letters...</p>
                </div>
              </div>
            ) : receivedLetters.length > 0 ? (
              <div className="space-y-4" data-testid="received-letters">
                {receivedLetters.map((letter, index) => (
                  <Card
                    key={letter.letter_id}
                    onClick={() => navigate(letter.series_id ? `/series/${letter.series_id}/letter/${letter.week_number}` : `/letter/${letter.week_number}`)}
                    className="p-6 cursor-pointer bg-parchment border-[#E5E0D0] hover:bg-parchment-paper hover:shadow-soft-paper hover:border-sepia/30 transition-all duration-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`received-letter-${letter.week_number}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Mail className="w-6 h-6 text-wine" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-playfair text-lg text-charcoal">Week {letter.week_number}</span>
                            {letter.teaser && (
                              <span className="text-xs font-lora text-wine bg-rose-muted/20 px-2 py-0.5 rounded-full">
                                Free Preview
                              </span>
                            )}
                          </div>
                          <h3 className="font-playfair text-xl text-charcoal mb-1">{letter.title}</h3>
                          <p className="font-dancing text-sepia">{letter.date}</p>
                          {letter.pov && (
                            <p className="font-lora text-sm text-charcoal-muted mt-2">
                              {letter.pov}'s {getLetterTypeLabel(letter.letter_type)}
                              {letter.series_id && (
                                <span className="text-charcoal-light"> · {letter.series_id === 'summer-1978' ? 'Summer 1978' : 'Victorian England'}</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-charcoal-light">
                        <CheckCircle className="w-5 h-5 text-wine" />
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Inbox className="w-12 h-12 text-charcoal-light mx-auto mb-4" />
                <h2 className="font-playfair text-2xl text-charcoal mb-2">No Letters Yet</h2>
                <p className="font-lora text-charcoal-muted mb-6">
                  Subscribe to start receiving weekly letters from Clara and James.
                </p>
                <Button onClick={() => navigate('/subscription')} className="btn-primary">
                  Subscribe Now
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Full Archive Tab */}
          <TabsContent value="archive">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Feather className="w-8 h-8 text-wine mx-auto mb-4 animate-pulse" />
                  <p className="font-lora text-charcoal-muted">Loading archive...</p>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="letters-grid">
                {allWeeks.map((week, index) => {
                  const letter = getLetterForWeek(week);
                  const accessible = isLetterAccessible(letter);
                  const isTeaser = letter?.teaser;
                  
                  return (
                    <Card
                      key={`${letter?.series_id || 'unknown'}-${week}`}
                      onClick={() => letter && accessible && navigate(letter.series_id ? `/series/${letter.series_id}/letter/${week}` : `/letter/${week}`)}
                      className={`p-5 transition-all duration-300 ${
                        letter && accessible
                          ? 'cursor-pointer bg-parchment border-[#E5E0D0] hover:bg-parchment-paper hover:shadow-soft-paper hover:border-sepia/30'
                          : 'bg-parchment-subtle/50 border-[#E5E0D0]/50'
                      }`}
                      style={{ animationDelay: `${Math.min(index * 30, 500)}ms` }}
                      data-testid={`letter-card-${week}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-playfair text-2xl text-charcoal">
                            {String(week).padStart(2, '0')}
                          </span>
                          {isTeaser && (
                            <span className="text-xs font-lora text-wine bg-rose-muted/20 px-2 py-0.5 rounded-full">
                              Free
                            </span>
                          )}
                        </div>
                        {letter && accessible ? (
                          <Mail className="w-5 h-5 text-rose-muted" />
                        ) : (
                          <Lock className="w-4 h-4 text-charcoal-light" />
                        )}
                      </div>
                      
                      {letter ? (
                        <>
                          <h3 className={`font-playfair text-base mb-1 ${accessible ? 'text-charcoal' : 'text-charcoal-muted'}`}>
                            {letter.title}
                          </h3>
                          <p className={`font-dancing text-sm ${accessible ? 'text-sepia' : 'text-charcoal-light'}`}>
                            {letter.date}
                          </p>
                          {letter.pov && (
                            <p className="font-lora text-xs text-charcoal-light mt-2">
                              {letter.pov} · {getLetterTypeLabel(letter.letter_type)}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="font-lora text-sm text-charcoal-light italic">
                          Coming soon...
                        </p>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Upcoming Letters Tab */}
          <TabsContent value="upcoming">
            <div className="space-y-4" data-testid="upcoming-letters">
              {!isSubscribed && (
                <Card className="p-6 bg-rose-muted/10 border border-rose-muted/30 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-wine" />
                    </div>
                    <div>
                      <h3 className="font-playfair text-lg text-charcoal mb-1">Subscribe to Unlock</h3>
                      <p className="font-lora text-charcoal-muted mb-4">
                        Get access to all 52 letters as they're released weekly.
                      </p>
                      <Button onClick={() => navigate('/subscription')} className="btn-primary">
                        Subscribe for $7/month
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {upcomingWeeks.slice(0, 12).map((week, index) => {
                const letter = getLetterForWeek(week);
                
                return (
                  <Card
                    key={week}
                    className="p-6 bg-parchment-subtle/50 border-[#E5E0D0]/50"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`upcoming-week-${week}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-parchment-paper border border-[#E5E0D0] rounded-full flex items-center justify-center flex-shrink-0">
                          <Clock className="w-6 h-6 text-charcoal-light" />
                        </div>
                        <div>
                          <span className="font-playfair text-lg text-charcoal-muted">Week {week}</span>
                          {letter ? (
                            <>
                              <h3 className="font-playfair text-xl text-charcoal-muted mt-1">{letter.title}</h3>
                              <p className="font-dancing text-charcoal-light">{letter.date}</p>
                            </>
                          ) : (
                            <p className="font-lora text-charcoal-light mt-1 italic">Letter in preparation...</p>
                          )}
                        </div>
                      </div>
                      <Lock className="w-5 h-5 text-charcoal-light" />
                    </div>
                  </Card>
                );
              })}

              {upcomingWeeks.length > 12 && (
                <p className="text-center font-lora text-charcoal-muted py-4">
                  And {upcomingWeeks.length - 12} more letters to come...
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E0D0] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="font-dancing text-lg text-sepia">"Moments preserved in ink"</p>
          <p className="font-lora text-sm text-charcoal-muted">© 2026 Unspoken Hearts</p>
        </div>
      </footer>
    </div>
  );
}
